import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Dialog, Toast } from 'antd-mobile';
import outStyles from '../../index.module.scss';
import styles from './index.module.scss';
import { EnergyConsumptionAdminList, EnergyConsumptionPlanGetStaffOrgMapping } from '@/api/energy';
import { CheckOutline } from 'antd-mobile-icons';
import { DateItem, OperationType } from '../..';

import ConfigurationTime from '../ConfigurationTime';
import { formatTimeRangesWithTomorrow } from '../../util';
import dayjs from 'dayjs';

type BatchConfigurationProp = {
  operationType?: OperationType;
  mediumList?: EnergyConsumptionAdminList[];
  areaList?: EnergyConsumptionPlanGetStaffOrgMapping['areaList'];
  dateList?: DateItem[];
  orgInfo?: { id: string; name: string };
  conflictedTime?: Map<string, any>;
  onCancel: () => void;
  onOk: (params: any) => void;
};

const BatchConfiguration = ({
  onCancel,
  onOk,
  operationType,
  orgInfo,
  conflictedTime,
  mediumList = [],
  areaList = [],
  dateList = [],
}: BatchConfigurationProp) => {
  // 步骤 1: 选择介质和日期，步骤 2: 配置时间
  const [step, setStep] = useState(1);
  const [selectedMediums, setSelectedMediums] = useState<number[]>([]);
  const [selectedDatesMap, setSelectedDatesMap] = useState<Record<string, number[]>>({});
  const [disabledTimes, setDisabledTimes] = useState<Array<string>>([]);

  const ref = useRef<any>();

  // 介质单选/多选
  const handleMediumClick = (id: number) => {
    setSelectedMediums(prev => (prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]));
  };
  // 介质全选
  const handleMediumSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedMediums(mediumList.map(m => m.energyMediumId).filter((id): id is number => id !== undefined));
    } else {
      setSelectedMediums([]);
    }
  };

  // 日期单选/多选（针对每个区）
  const handleDateClick = (zoneId: number, idx: number) => {
    setSelectedDatesMap(prev => {
      const prevArr = prev[zoneId] || [];
      const newArr = prevArr.includes(idx) ? prevArr.filter(i => i !== idx) : [...prevArr, idx];
      return { ...prev, [zoneId]: newArr };
    });
  };
  // 日期全选（针对每个区）
  const handleDateSelectAll = (checked: boolean, zoneId: number) => {
    setSelectedDatesMap(prev => ({
      ...prev,
      [zoneId]: checked ? dateList.map((_, idx) => idx) : [],
    }));
  };

  // 下一步
  const handleNext = () => {
    if (conflictedTime) {
      const defaultDisabledTime: Array<any> = [];
      selectedMediums?.forEach(mediumId => {
        Object.entries(selectedDatesMap).forEach(([areaId, dateObj]) => {
          const selectedDates = dateObj.map(idx => dateList[idx]?.date);
          selectedDates.forEach(date => {
            const curConflictedTime = conflictedTime.get(`${mediumId}/${orgInfo?.id}/${areaId}/${date}`) ?? [];

            defaultDisabledTime.push(...curConflictedTime);
          });
        });
      });
      setDisabledTimes(defaultDisabledTime);
    }
    setStep(2);
  };
  // 上一步
  const handlePrev = () => setStep(1);
  // 确定
  const handleConfirm = () => {
    let dialogContent =
      operationType === OperationType.BatchClear
        ? '确定批量清除配置时段？'
        : '批量配置的时间段将覆盖原有数据，是否继续？';

    Dialog.confirm({
      content: dialogContent,
      confirmText: '确定',
      onConfirm: () => {
        const timeRanges = ref.current?.getTimeRangeList() || [];

        // 组装 map 结构
        const areaMediumDateDetailMap: {
          [mediumId: string]: {
            [areaId: string]: {
              [date: string]: any[];
            };
          };
        } = {};

        selectedMediums.forEach(mediumId => {
          if (!areaMediumDateDetailMap[mediumId]) areaMediumDateDetailMap[mediumId] = {};
          Object.entries(selectedDatesMap).forEach(([areaId, dateObj]) => {
            if (!areaMediumDateDetailMap[mediumId][areaId]) areaMediumDateDetailMap[mediumId][areaId] = {};

            const selectedDates = dateObj.map(idx => dateList[idx]?.date);

            selectedDates.forEach(date => {
              areaMediumDateDetailMap[mediumId][areaId][date] =
                operationType === OperationType.BatchClear ? [] : formatTimeRangesWithTomorrow(date, timeRanges);
            });
          });
        });
        onOk(areaMediumDateDetailMap);
      },
    });
  };

  return (
    <>
      {step === 1 && (
        <div className={styles.container}>
          <div className={outStyles.popupTitleContainer}>
            <div className={outStyles.popupTitle}>批量配置</div>
          </div>
          <div className={outStyles.popupContent}>
            <div className={styles.listContainer}>
              <div className={styles.titleContainer}>
                <div>
                  选择介质 <span style={{ color: '#999999', fontSize: 13 }}>（多选）</span>
                </div>
                <Checkbox
                  className={outStyles.linkBtn}
                  indeterminate={selectedMediums.length > 0 && selectedMediums.length < mediumList.length}
                  checked={mediumList.length > 0 ? mediumList.length === selectedMediums.length : false}
                  onChange={handleMediumSelectAll}
                >
                  全选
                </Checkbox>
              </div>
              <div className={styles.list}>
                {mediumList.map(medium => {
                  const id = medium.energyMediumId;
                  const selected = id !== undefined && selectedMediums.includes(id);
                  return (
                    <div
                      key={String(id)}
                      className={`${styles.listItem} ${selected ? styles.listItemSelected : ''}`}
                      onClick={() => id !== undefined && handleMediumClick(id)}
                      style={{ position: 'relative' }}
                    >
                      {medium.energyMediumName}
                      {selected ? (
                        <div className={styles.selectedIcon}>
                          <CheckOutline color="#fff" />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {areaList.map(area => {
              const zoneId = area.id!;
              const selectedDates = selectedDatesMap[zoneId] || [];
              return (
                <div className={styles.listContainer} key={area.zoneName}>
                  <div className={styles.titleContainer}>
                    <div>
                      {area.zoneName} <span style={{ color: '#999999', fontSize: 13 }}>（多选）</span>
                    </div>
                    <Checkbox
                      className={outStyles.linkBtn}
                      indeterminate={selectedDates.length > 0 && selectedDates.length < dateList.length}
                      checked={dateList.length > 0 ? dateList.length === selectedDates.length : false}
                      onChange={checked => handleDateSelectAll(checked, zoneId)}
                    >
                      全选
                    </Checkbox>
                  </div>
                  <div className={styles.list}>
                    {dateList.map((item, idx) => {
                      const selected = selectedDates.includes(idx);
                      return (
                        <div
                          key={idx}
                          className={`${styles.listItem} ${selected ? styles.listItemSelected : ''}`}
                          onClick={() => handleDateClick(zoneId, idx)}
                          style={{ position: 'relative' }}
                        >
                          {item.date}
                          {selected ? (
                            <div className={styles.selectedIcon}>
                              <CheckOutline color="#fff" />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {operationType === OperationType.BatchConfig && step === 1 && (
            <div className={outStyles.actionBar}>
              <Button size="large" onClick={onCancel}>
                取消
              </Button>
              <Button size="large" color="primary" fill="solid" onClick={handleNext}>
                下一步
              </Button>
            </div>
          )}
          {operationType === OperationType.BatchClear && (
            <div className={outStyles.actionBar}>
              <Button size="large" onClick={onCancel}>
                取消
              </Button>
              <Button size="large" color="danger" fill="solid" onClick={handleConfirm}>
                批量清除
              </Button>
            </div>
          )}
        </div>
      )}
      {step === 2 && (
        <div className={styles.listContainer}>
          <ConfigurationTime
            disabledTimes={disabledTimes}
            configArea={{ date: dayjs().format('YYYY-MM-DD') }}
            ref={ref}
            footer={
              <div className={outStyles.actionBar}>
                <Button size="large" onClick={handlePrev}>
                  上一步
                </Button>
                <Button size="large" color="primary" fill="solid" onClick={handleConfirm}>
                  确定
                </Button>
              </div>
            }
          />
        </div>
      )}
    </>
  );
};

export default BatchConfiguration;
